import type { FC, ReactElement } from "react";
import React, { useEffect } from "react";
import { useLocation } from "react-router";
import type { NavRouteTree } from "@/routerConfig";
import { useAuth0 } from "@auth0/auth0-react";
import classNames from "classnames";
import AppHeader from "@/components/AppHeader";
import NavMenu from "@/components/NavMenu";
import SuspenseLoader from "@/components/SuspenseLoader";
import { identifyAndTrackUser } from "@/helpers/user.helpers";
import useMenuToggleState from "@/hooks/useMenuToggleState";
import { useReturnUrlRedirect } from "@/hooks/useReturnUrlRedirect";
import { findParentForPath } from "@/routerConfig/router.helpers";
import css from "./AppLayout.module.css";

type AppLayoutProps = {
  menuItems: NavRouteTree;
  children: ReactElement;
};

/**
 *
 * @returns Header and NavMenu
 */
const AppLayout: FC<AppLayoutProps> = ({ menuItems, children }) => {
  useReturnUrlRedirect();
  const [menuExpanded, setMenuExpanded] = useMenuToggleState();
  const { pathname } = useLocation();
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  identifyAndTrackUser();

  if (!isAuthenticated) {
    return (
      <div className={css.authLoadingContainer}>
        <SuspenseLoader height={200} size="md" />
      </div>
    );
  }

  const parent = findParentForPath(menuItems, pathname);
  return (
    <div className={classNames("root-layout", { expanded: menuExpanded })}>
      <AppHeader
        menuToggle={() => {
          setMenuExpanded(!menuExpanded);
        }}
        menuExpanded={menuExpanded}
      />

      <nav id="app-nav">
        <NavMenu menuItems={menuItems} activeSectionLabel={parent?.label} />
      </nav>

      {children}
    </div>
  );
};

export default AppLayout;
